import * as APIModels from "../../../models";
import { useState, useEffect } from "react";
import DocumentView from "./DocumentView";
import PDFViewer from "./PDFViewer";
import NewCase from "./forms/NewCase";
import ExistingCase from "./forms/ExistingCase";
import DFS from "./forms/DFS";
import ServiceDocuments from "./forms/ServiceDocuments";
import Order from "./forms/Order";
import { useCallback } from "react";
import {
    FileService,
    PartyService,
    SubmissionService,
} from "../../../services/SubmissionService";
import { MessageRulesService } from "../../../services/ClientService";
import HoldModal from "./modals/HoldModal";
import AttorneyModal from "./modals/AttorneyModal";
import FailModal from "./modals/FailModal";
import ChecSimilarModal from "./modals/CheckSimilarModal";
import AuthenticationModal from "./modals/AttorneyAuthModal";
import ValidationCheckModal from "./modals/ValidationCheckModal";
import FilingPartyModal from "./modals/FilingPartyModal";
import { Globals } from "../../../helpers/globals";
//import { PDFViewer } from "pdfjs-dist/web/pdf_viewer";

export default function SubmissionForm({
    submission,
    setNextSubmission,
    filter,
}: {
    submission: APIModels.Submission;
    setNextSubmission: (next: APIModels.Submission) => void;
    filter?: string;
}) {
    const [currentSubmission, setCurrentSubmission] = useState(submission);
    const [currentCaseData, setCurrentCaseData] = useState(submission.caseData);
    const [selectedDocument, setSelectedDocument] = useState(
        submission.documents
            ? submission.documents[0]
            : new APIModels.Document()
    );
    const [checkSimilarModalShow, setCheckSimilarModalShow] = useState(false);
    const [validationModalShow, setValidationModalShow] = useState(false);
    const [holdModalShow, setHoldModalShow] = useState(false);
    const [failModalShow, setFailModalShow] = useState(false);
    const [attorneyModalShow, setAttorneyModalShow] = useState(false);
    const [filingPartyModalShow, setFilingPartyModalShow] = useState(false);
    const [showPdfViewer, setShowPdfViewer] = useState(false);
    const [authenticationModal, setAuthenticationModal] = useState(false);
    const [efmepdfUrl, setEfmepdfUrl] = useState<string | null>(null);
    const [efmepdfWarning, setEfmepdfWarning] = useState<string | null>(null);

    useEffect(() => {
        MessageRulesService.rule({
            attorneyId: submission.attorneyID!,
            county: submission.caseData?.county,
            filename: submission.filename,
            claimAmount: parseInt(submission.caseData?.claimAmount ?? "0"),
        }).then(
            (resp) => {
                if (resp.successful && resp.data != "") {
                    alert(resp.data);
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }, [submission]);

    useEffect(() => {
        setCurrentSubmission(submission);

        setSelectedDocument(
            submission.documents
                ? submission.documents[0]
                : new APIModels.Document()
        );
    }, [submission]);

    useEffect(() => {
        if (
            submission &&
            submission.filename &&
            submission.filename.indexOf("-d-") > -1
        ) {
            setShowPdfViewer(true);

            const summons = submission.documents?.find((d) =>
                d.filename?.includes("DFS_Summons")
            );

            if (summons) {
                setSelectedDocument(summons);
            }
        }
    }, [currentSubmission]);

    const onSelectDocument = (doc: APIModels.Document, index: number) => {
        setSelectedDocument(doc);
    };

    const onCaseDataChange = (caseData: APIModels.CaseData) => {
        setCurrentCaseData(caseData);
    };

    const cancel = (submissionId: string) => {
        SubmissionService.cancel({ submissionId, body: {} }).then(
            (resp) => {
                window.location.href = "/queue/" + filter;
            },
            (error) => {
                console.log(error);
                //setError(error);
            }
        );
    };

    const hold = (submissionId: string, reason: string, details: string) => {
        SubmissionService.hold({
            submissionId,
            body: { defaultMessage: parseInt(reason), message: details },
        }).then(
            (resp) => {
                window.location.href = "/queue/" + filter;
            },
            (error) => {
                console.log(error);
                //setError(error);
            }
        );
    };

    const fail = (submissionId: string, reason: string, details: string) => {
        SubmissionService.fail({
            submissionId,
            body: { defaultMessage: parseInt(reason), message: details },
        }).then(
            (resp) => {
                window.location.href = "/queue/" + filter;
            },
            (error) => {
                console.log(error);
                //setError(error);
            }
        );
    };

    const getEfmepdf = () => {
        setEfmepdfUrl(null);
        setEfmepdfWarning(null);

        const efmepdfDoc = currentSubmission.documents?.find((d) =>
            d.filename?.toUpperCase().includes("EFMETEXTDATAINFO.EFMEPDF")
        );

        if (!efmepdfDoc || !currentSubmission.id) {
            setEfmepdfWarning(
                "EFMETEXTDATAINFO.efmepdf was not found for this submission."
            );
            return;
        }

        FileService.get({
            submissionId: currentSubmission.id,
            documentId: efmepdfDoc.filename!,
            source: "",
        }).then(
            (resp) => {
                resp.json().then((json) => {
                    if (json.url) {
                        setEfmepdfUrl(json.url);
                    } else {
                        setEfmepdfWarning(
                            "EFMETEXTDATAINFO.efmepdf was not found for this submission."
                        );
                    }
                });
            },
            (error) => {
                console.log(error);
                setEfmepdfWarning(
                    "Failed to retrieve EFMETEXTDATAINFO.efmepdf."
                );
            }
        );
    };

    const renderForm = useCallback(
        (filename: string) => {
            if (filename.indexOf("-a-0-") > 0) {
                return (
                    <NewCase
                        submission={currentSubmission}
                        onSelectDocument={onSelectDocument}
                        filter={filter}
                        currentDocument={selectedDocument}
                        onCaseDataChange={onCaseDataChange}
                    />
                );
            } else if (filename.indexOf("-d-0-") > 0) {
                return <DFS submission={currentSubmission} filter={filter} />;
            } else if (filename.indexOf("-s-0-") > 0) {
                return (
                    <ServiceDocuments
                        submission={currentSubmission}
                        filter={filter}
                    />
                );
            } else if (filename.indexOf("-o-0-") > 0) {
                return (
                    <Order
                        submission={currentSubmission}
                        onSelectDocument={onSelectDocument}
                        setNextSubmission={setNextSubmission}
                        filter={filter}
                    />
                );
            } else {
                return (
                    <ExistingCase
                        submission={currentSubmission}
                        onSelectDocument={onSelectDocument}
                        setNextSubmission={setNextSubmission}
                        filter={filter}
                        onCaseDataChange={onCaseDataChange}
                    />
                );
            }
        },
        [currentSubmission, selectedDocument]
    );

    const onSave = (
        leadAttorney: APIModels.Party,
        isNewLeadAttorney?: boolean
    ) => {
        console.log({ currentSubmission: currentSubmission.id });
        if (currentSubmission.id) {
            // "Associate with filing parties" is no longer set here: it's a checkbox
            // under Submit on the existing-case form, applied to the lead attorney
            // when the submission is submitted.
            PartyService.leadAttorney({
                submissionId: currentSubmission.id,
                newLeadAttorney: isNewLeadAttorney ?? false,
                body: leadAttorney,
            })
                .then((res) => {
                    if (res.data && res.successful) {
                        const caseData = currentSubmission.caseData;
                        if (caseData) {
                            caseData.leadAttorney = leadAttorney;
                            setCurrentSubmission({
                                caseData: caseData,
                                ...currentSubmission,
                            });
                        }
                        console.log({ ...res.data });
                        setAttorneyModalShow(false);
                    } else {
                        console.error(res?.errorMessage);
                    }
                })
                .catch(() => {
                    console.log("error");
                });
        }
    };

    const currentUser = Globals.currentUserValue;
    return (
        <div className="row">
            {currentSubmission.id && (
                <>
                    {currentUser.role!.key! >= 500 && (
                        <AuthenticationModal
                            attorneyId={currentSubmission.attorneyID ?? ""}
                            attorneyName={
                                currentSubmission.attorney?.fullName ?? ""
                            }
                            portalId={currentSubmission.portalIdentifier ?? ""}
                            isOpen={authenticationModal}
                            handleClose={() => setAuthenticationModal(false)}
                        />
                    )}

                    <ChecSimilarModal
                        submission={currentSubmission}
                        isOpen={checkSimilarModalShow}
                        handleClose={() => setCheckSimilarModalShow(false)}
                        currentCounty={currentCaseData?.county}
                    />
                    <ValidationCheckModal
                        submission={currentSubmission}
                        isOpen={validationModalShow}
                        handleClose={() => setValidationModalShow(false)}
                        userRoleCode={currentUser.role?.key}
                    />
                    <AttorneyModal
                        show={attorneyModalShow}
                        onClose={() => setAttorneyModalShow(false)}
                        onSave={onSave}
                        attorney={currentSubmission.caseData?.leadAttorney}
                    />
                    <FilingPartyModal
                        show={filingPartyModalShow}
                        onClose={() => setFilingPartyModalShow(false)}
                        submission={currentSubmission}
                    />
                    <HoldModal
                        show={holdModalShow}
                        onHold={(reason: string, details: string) => {
                            setHoldModalShow(false);
                            hold(submission.id!, reason, details);
                        }}
                        onClose={() => setHoldModalShow(false)}
                    />
                    <FailModal
                        show={failModalShow}
                        onFail={(reason: string, details: string) => {
                            setFailModalShow(false);
                            fail(submission.id!, reason, details);
                        }}
                        onCancel={() => setFailModalShow(false)}
                    />
                    <div className="col-lg-6">
                        <div className="card">
                            <div className="card-body py-2 px-3 border-bottom border-light">
                                <div className="d-flex py-1">
                                    <div className="flex-1">
                                        <h5 className="mt-0 mb-0 font-15">
                                            <a
                                                href="contacts-profile.html"
                                                className="text-reset"
                                            >
                                                {`${submission.attorney?.lastName}, ${submission.attorney?.firstName}`}
                                            </a>
                                            <a
                                                href="javascript: void(0);"
                                                className="text-reset font-19 py-1 px-2 d-inline-block"
                                                title="Place file in hold queue"
                                                onClick={() =>
                                                    setAttorneyModalShow(true)
                                                }
                                            >
                                                <i
                                                    className="fe-user"
                                                    data-bs-container="#tooltip-container"
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="View attorney"
                                                    data-bs-original-title="Voice Call"
                                                    aria-label="Voice Call"
                                                ></i>
                                            </a>
                                        </h5>
                                        {submission.upload?.uploadedBy && (
                                            <p
                                                className="mb-0 text-muted font-13"
                                                title="Contact the uploader with questions about this submission"
                                            >
                                                Uploaded by:{" "}
                                                {submission.upload.uploadedBy.includes(
                                                    "@"
                                                ) ? (
                                                    <a
                                                        href={`mailto:${submission.upload.uploadedBy}`}
                                                    >
                                                        {
                                                            submission.upload
                                                                .uploadedBy
                                                        }
                                                    </a>
                                                ) : (
                                                    submission.upload.uploadedBy
                                                )}
                                            </p>
                                        )}
                                    </div>
                                    <div id="tooltip-container">
                                        {currentUser.role!.key! >= 500 && (
                                            <a
                                                href="javascript: void(0);"
                                                className="text-reset font-19 py-1 px-2 d-inline-block"
                                                title="View PortalConfiguration"
                                                onClick={() =>
                                                    setAuthenticationModal(true)
                                                }
                                            >
                                                <i
                                                    className="fe-lock"
                                                    data-bs-container="#tooltip-container"
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="View PortalConfiguration"
                                                    data-bs-original-title="Zoom Call"
                                                    aria-label="Zoom Call"
                                                ></i>
                                            </a>
                                        )}
                                        <a
                                            href="javascript: void(0);"
                                            className="text-reset font-19 py-1 px-2 d-inline-block"
                                            title="Check similar cases"
                                            onClick={() =>
                                                setCheckSimilarModalShow(true)
                                            }
                                        >
                                            <i
                                                className="fe-help-circle"
                                                data-bs-container="#tooltip-container"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Check similar cases"
                                                data-bs-original-title="Zoom Call"
                                                aria-label="Zoom Call"
                                            ></i>
                                        </a>
                                        <a
                                            href="javascript: void(0);"
                                            className="text-reset font-19 py-1 px-2 d-inline-block"
                                            title="View filing party responsible"
                                            onClick={() =>
                                                setFilingPartyModalShow(true)
                                            }
                                        >
                                            <i
                                                className="fe-briefcase"
                                                data-bs-container="#tooltip-container"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="View filing party responsible"
                                                aria-label="View filing party responsible"
                                            ></i>
                                        </a>
                                        {currentUser.role!.key! >= 600 && (
                                            <a
                                                href="javascript: void(0);"
                                                className="text-reset font-19 py-1 px-2 d-inline-block"
                                                title="Validate submission"
                                                onClick={() =>
                                                    setValidationModalShow(true)
                                                }
                                            >
                                                <i
                                                    className="fe-check-circle"
                                                    data-bs-container="#tooltip-container"
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="Validate submission"
                                                    data-bs-original-title="Validate"
                                                    aria-label="Validate"
                                                ></i>
                                            </a>
                                        )}
                                        <a
                                            href="javascript: void(0);"
                                            className="text-reset font-19 py-1 px-2 d-inline-block"
                                            title="Place file in hold queue"
                                            onClick={() =>
                                                setHoldModalShow(true)
                                            }
                                        >
                                            <i
                                                className="fe-pause"
                                                data-bs-container="#tooltip-container"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Place file in hold queue"
                                                data-bs-original-title="Voice Call"
                                                aria-label="Voice Call"
                                            ></i>
                                        </a>
                                        <a
                                            href="javascript: void(0);"
                                            className="text-reset font-19 py-1 px-2 d-inline-block"
                                            title="Stop working on current file."
                                            onClick={() =>
                                                cancel(submission.id!)
                                            }
                                        >
                                            <i
                                                className="fe-stop-circle"
                                                data-bs-container="#tooltip-container"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Stop working on current file."
                                                data-bs-original-title="Video Call"
                                                aria-label="Video Call"
                                            ></i>
                                        </a>
                                        <a
                                            href="javascript: void(0);"
                                            className="text-reset font-19 py-1 px-2 d-inline-block"
                                            title="Fail file back to submitting attorney."
                                            onClick={() =>
                                                setFailModalShow(true)
                                            }
                                        >
                                            <i
                                                className="fe-trash-2"
                                                data-bs-container="#tooltip-container"
                                                data-bs-toggle="tooltip"
                                                data-bs-placement="top"
                                                title="Fail file back to submitting attorney."
                                                data-bs-original-title="Add Users"
                                                aria-label="Add Users"
                                            ></i>
                                        </a>
                                        {process.env
                                            .REACT_APP_EFME_FF_DEBUG_EFMEPDF ===
                                            "1" && (
                                            <a
                                                href="javascript: void(0);"
                                                className="text-reset font-19 py-1 px-2 d-inline-block"
                                                title="Debug: Get EFMETEXTDATAINFO.efmepdf"
                                                onClick={getEfmepdf}
                                            >
                                                <i
                                                    className="fe-file-text"
                                                    data-bs-container="#tooltip-container"
                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    title="Debug: Get EFMETEXTDATAINFO.efmepdf"
                                                    aria-label="Debug: Get EFMETEXTDATAINFO.efmepdf"
                                                ></i>
                                            </a>
                                        )}
                                    </div>
                                </div>
                                {process.env.REACT_APP_EFME_FF_DEBUG_EFMEPDF ===
                                    "1" &&
                                    (efmepdfWarning || efmepdfUrl) && (
                                        <div className="d-flex py-1">
                                            {efmepdfWarning && (
                                                <div className="alert alert-warning mb-0 w-100">
                                                    {efmepdfWarning}
                                                </div>
                                            )}
                                            {efmepdfUrl && (
                                                <a
                                                    href={efmepdfUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    download="EFMETEXTDATAINFO.efmepdf"
                                                >
                                                    Download
                                                    EFMETEXTDATAINFO.efmepdf
                                                </a>
                                            )}
                                        </div>
                                    )}
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-lg-12">
                                        {renderForm(
                                            currentSubmission.filename!
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        <div
                            style={{
                                position: "sticky",
                                top: "80px",
                                zIndex: 100,
                                maxHeight: "calc(100vh - 40px)",
                                overflowY: "auto",
                            }}
                        >
                            <div className="row">
                                <div className="col-sm-4">
                                    <button
                                        className="btn btn-primary me-5"
                                        onClick={() => {
                                            if (currentSubmission.documents) {
                                                let currentIndex =
                                                    currentSubmission.documents.indexOf(
                                                        selectedDocument
                                                    );
                                                if (currentIndex - 1 >= 0) {
                                                    currentIndex =
                                                        currentIndex - 1;
                                                } else {
                                                    currentIndex =
                                                        currentSubmission
                                                            .documents.length -
                                                        1;
                                                }
                                                setSelectedDocument(
                                                    currentSubmission.documents[
                                                        currentIndex
                                                    ]
                                                );
                                            }
                                        }}
                                    >
                                        Previous
                                    </button>
                                </div>
                                <div className="col-sm-4 text-center">
                                    <button
                                        className="btn btn-primary"
                                        onClick={() =>
                                            setShowPdfViewer(!showPdfViewer)
                                        }
                                    >
                                        PDF Viewer
                                    </button>
                                </div>
                                <div className="col-sm-4">
                                    <button
                                        className="btn btn-primary"
                                        style={{ float: "right" }}
                                        onClick={() => {
                                            if (currentSubmission.documents) {
                                                let currentIndex =
                                                    currentSubmission.documents.indexOf(
                                                        selectedDocument
                                                    );
                                                if (
                                                    currentIndex + 1 <
                                                    currentSubmission.documents
                                                        .length
                                                ) {
                                                    currentIndex =
                                                        currentIndex + 1;
                                                } else {
                                                    currentIndex = 0;
                                                }
                                                setSelectedDocument(
                                                    currentSubmission.documents[
                                                        currentIndex
                                                    ]
                                                );
                                            }
                                        }}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                            {selectedDocument &&
                                selectedDocument.id &&
                                (showPdfViewer ? (
                                    <PDFViewer
                                        submissionId={currentSubmission.id!}
                                        documentId={selectedDocument.id!}
                                        filename={selectedDocument.filename!}
                                    />
                                ) : (
                                    <DocumentView
                                        submissionId={currentSubmission.id!}
                                        documentId={selectedDocument.id!}
                                        filename={selectedDocument.filename!}
                                    />
                                ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
